import { ATTR_ABBR, ATTR_KEYS, type AttrKey, CARD_L_ATTRS, type RatingState, type TierId } from '#shared/core/ratings';
import type { RatingCard } from '#shared/schemas/rating';
import { fill } from '@/copy';
import { ratingText, tierName, tierOf } from '@/lib/rating';
import { copy } from './copy';
import { hashString } from './text';

/**
 * The card's view model: what to print, derived from the player and the RatingCard. Pure, so the
 * display rules of add-ratings.md section 4 are tested without a DOM.
 */

/** The bits of a player the card reads. PlayerListItem and PlayerDetail both fit. */
export interface CardPlayer {
  id: string;
  displayName: string;
  nickname: string | null;
  jersey: number | null;
}

const DAY_MS = 86_400_000;

/** Attributes under this confidence print "??" (add-ratings.md section 4). */
export const ATTR_MIN_CONF = 0.15;

export type CardState = RatingState;

/** The state of a card; no rating at all, or a rating with no OVR yet, is unrated. */
export function cardState(rating: RatingCard | null | undefined): CardState {
  if (!rating || rating.state === 'unrated' || rating.ovr === null) return 'unrated';
  return rating.state;
}

export function cardOvr(rating: RatingCard | null | undefined): number | null {
  return cardState(rating) === 'unrated' ? null : (rating?.ovr ?? null);
}

/** Tier of a card: null while unrated. Trusts the OVR when `tier` and `ovr` disagree. */
export function cardTier(rating: RatingCard | null | undefined): TierId | null {
  return tierOf(cardOvr(rating));
}

/** The label in the tier tag: the tier name, or "Combine pending". */
export function tierLabel(tier: TierId | null): string {
  return tier ? tierName(tier) : copy.unratedTag;
}

export interface AttrView {
  key: AttrKey;
  label: string;
  /** The number to print, null for "??". */
  value: number | null;
  /** 0 to 1 bar fill, 0 when unknown. */
  fraction: number;
  /** 0 to 9 gets the little snowflake on the large card. */
  singleDigit: boolean;
}

export function attrView(rating: RatingCard | null | undefined, key: AttrKey): AttrView {
  const cell = rating?.attrs?.[key];
  const known = !!cell && cell.value !== null && cell.conf >= ATTR_MIN_CONF;
  const value = known ? (cell.value as number) : null;
  return {
    key,
    label: ATTR_ABBR[key],
    value,
    fraction: value === null ? 0 : Math.max(0, Math.min(1, value / 99)),
    singleDigit: value !== null && value <= 9,
  };
}

/** The fixed six of the large card (R19). */
export function largeAttrs(rating: RatingCard | null | undefined): AttrView[] {
  return CARD_L_ATTRS.map((k) => attrView(rating, k));
}

/**
 * The top `n` known attributes for the medium card: highest value first, ties in ATTR_KEYS order.
 * When fewer than `n` are known the rest are "??" slots taken from the fixed six, so the row
 * keeps its shape.
 */
export function topAttrs(rating: RatingCard | null | undefined, n = 3): AttrView[] {
  const all = ATTR_KEYS.map((k, i) => ({ v: attrView(rating, k), i }));
  const known = all
    .filter((x) => x.v.value !== null)
    .sort((a, b) => (b.v.value as number) - (a.v.value as number) || a.i - b.i)
    .slice(0, n)
    .map((x) => x.v);
  if (known.length >= n) return known;
  const taken = new Set(known.map((x) => x.key));
  const filler = CARD_L_ATTRS.filter((k) => !taken.has(k)).map((k) => attrView(null, k));
  return [...known, ...filler].slice(0, n);
}

/** Whole days between `then` and `now`, never negative. */
export function daysSince(then: number, now: number): number {
  return Math.max(0, Math.floor((now - then) / DAY_MS));
}

/**
 * The line under the name. Rated cards show the archetype; the other states say what is going on.
 * `now` is injected so tests do not depend on the clock.
 */
export function subline(rating: RatingCard | null | undefined, playerId: string, now: number): string {
  const state = cardState(rating);
  if (state === 'unrated') {
    const pool = copy.unratedLines;
    return pool[hashString(playerId) % pool.length] ?? pool[0] ?? '';
  }
  if (!rating) return '';
  if (state === 'provisional') {
    const left = Math.max(1, 5 - rating.ratedGames);
    return left === 1 ? copy.provisionalSubOne : fill(copy.provisionalSub, { n: left });
  }
  if (state === 'rusty') {
    if (rating.lastEvidenceAt === null) return copy.rustySubUnknown;
    const d = daysSince(rating.lastEvidenceAt, now);
    if (d === 0) return copy.rustySubToday;
    if (d === 1) return copy.rustySubYesterday;
    return fill(copy.rustySub, { n: d });
  }
  return rating.archetype ?? '';
}

/** The big line of the nameplate: the nickname when there is one. */
export function plateName(player: Pick<CardPlayer, 'displayName' | 'nickname'>): string {
  return player.nickname?.trim() || player.displayName;
}

/** Accessible name of a card. */
export function cardAriaLabel(player: CardPlayer, rating: RatingCard | null | undefined): string {
  const name = plateName(player);
  const ovr = cardOvr(rating);
  if (ovr === null) return fill(copy.label.unrated, { name });
  return fill(copy.label.rated, { name, ovr: ratingText(ovr), tier: tierName(cardTier(rating)) });
}

/** "#7" for the jersey chip, null when the player has no number. */
export function jerseyText(jersey: number | null): string | null {
  return jersey === null || jersey === undefined ? null : `#${jersey}`;
}
